/**
 * English: Top bar with hamburger (mobile), health badge, theme toggle, and logout.
 * Thai: แถบบน พร้อม hamburger (mobile), health badge, theme toggle และ logout
 */
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLayout } from '../../context/LayoutContext';
import { useApiHealth } from '../../hooks/useApiHealth';

export default function Header() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { setSidebarOpen } = useLayout();
  const { data: health, loading: healthLoading, error: healthError } = useApiHealth(true);

  const healthLabel = (() => {
    if (healthLoading) return t('layout.healthChecking');
    if (healthError) return t('layout.healthUnreachable');
    if (!health) return t('layout.healthUnknown');
    if (health.status === 'ok') return t('layout.healthOk');
    return t('layout.healthDegraded');
  })();

  const healthDot =
    healthLoading ? 'bg-app-text-dim animate-pulse' :
    healthError || !health ? 'bg-app-error' :
    health.status === 'ok' ? 'bg-app-success' :
    'bg-amber-500';

  return (
    <header className="app-glass-panel h-14 border-b border-app-border/80 flex items-center justify-between px-4 md:px-6 shrink-0 gap-3 md:gap-4 backdrop-blur-xl">
      <div className="min-w-0 flex items-center gap-3 flex-1">
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden p-1.5 -ml-1 rounded-lg text-app-text-sec hover:bg-app-muted hover:text-app-text transition-colors"
          aria-label={t('layout.openMenu')}
        >
          <Bars3Icon className="w-5 h-5" />
        </button>

        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-medium text-app-text-sec truncate hidden sm:block">
            {t('layout.subtitle')}
          </h2>
          <p className="text-[11px] text-app-text-dim mt-0.5 truncate hidden md:block">
            <span
              title={
                health?.checks?.postgresql && health?.checks?.factory_api
                  ? `PostgreSQL: ${health.checks.postgresql.detail}\nFactory: ${health.checks.factory_api.detail}`
                  : undefined
              }
            >
              {t('layout.backend')}: {healthLabel}
              {health?.version ? ` (v${health.version})` : ''}
            </span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-4 shrink-0">
        <div className="flex items-center gap-2" title={healthError || undefined}>
          <span className={`w-2 h-2 rounded-full shrink-0 ${healthDot}`} />
          <span className="text-xs text-app-text-dim hidden lg:inline max-w-[120px] truncate">
            {healthLabel}
          </span>
        </div>
        <button
          onClick={toggleTheme}
          className="p-1.5 rounded-lg text-app-text-sec hover:bg-app-muted hover:text-app-text transition-colors"
          title={theme === 'dark' ? t('layout.themeLight') : t('layout.themeDark')}
        >
          {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
        </button>
        <button
          onClick={logout}
          className="flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-xs text-app-text-sec hover:bg-app-muted hover:text-app-text transition-colors"
          title={t('layout.logout')}
        >
          <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
          <span className="hidden md:inline">{t('layout.signOut')}</span>
        </button>
      </div>
    </header>
  );
}
